import { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Loader2, Check, AlertTriangle, Lock, LockOpen, CheckCircle, Star, Camera, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const ACCEPTED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

type PhotoUploadStatus = "idle" | "uploading" | "uploaded" | "error";

interface PhotoStepProps {
  sessionToken: string;
  photoPreview: string | null;
  setPhotoPreview: (v: string | null) => void;
  setPhotoUrl: (v: string | null) => void;
  status: PhotoUploadStatus;
  setStatus: (v: PhotoUploadStatus) => void;
  errorMsg: string | null;
  setErrorMsg: (v: string | null) => void;
}

const StepPhotoCheckout = ({
  sessionToken,
  photoPreview,
  setPhotoPreview,
  setPhotoUrl,
  status,
  setStatus,
  errorMsg,
  setErrorMsg,
}: PhotoStepProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setErrorMsg(null);

    if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) {
      setStatus("error");
      setErrorMsg("Please upload a JPEG, PNG, or WebP image.");
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setStatus("error");
      setErrorMsg("Image is too large. Max size is 10 MB.");
      return;
    }

    // Local preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target?.result as string);
    reader.readAsDataURL(file);

    setStatus("uploading");
    try {
      const formData = new FormData();
      formData.append("photo", file);

      const response = await fetch(
        "https://qwipsvc.up.railway.app/api/session/upload-checkout-photo",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${sessionToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.detail || "Upload failed");
      }

      const data = await response.json();
      setPhotoUrl(data.photo_url);
      setStatus("uploaded");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err instanceof Error && err.message
          ? err.message
          : "Couldn't upload photo. Please try again."
      );
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // reset so the same file can be reselected
    e.target.value = "";
  };

  const reset = () => {
    setPhotoPreview(null);
    setPhotoUrl(null);
    setStatus("idle");
    setErrorMsg(null);
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Camera className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-xl font-semibold text-foreground">Photo of the pod</h3>
        <p className="text-muted-foreground">
          Snap a quick photo of the pod before ending your session.
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        onChange={onChange}
        className="hidden"
      />

      {photoPreview && status !== "idle" ? (
        <div className="space-y-3">
          <div className="relative rounded-xl overflow-hidden border border-border bg-muted">
            <img
              src={photoPreview}
              alt="Pod checkout"
              className="w-full max-h-64 object-cover"
            />
            {status === "uploading" && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            )}
            {status === "uploaded" && (
              <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1.5">
                <Check className="w-4 h-4" />
              </div>
            )}
          </div>

          {status === "uploaded" && (
            <p className="text-sm text-center text-green-600 font-medium">
              Photo uploaded
            </p>
          )}

          <button
            onClick={reset}
            disabled={status === "uploading"}
            className="w-full py-2.5 px-4 bg-muted hover:bg-muted/80 text-foreground rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            Retake / choose another
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          <button
            onClick={() => inputRef.current?.click()}
            className="w-full py-3.5 px-6 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <Camera className="w-5 h-5" />
            Take or upload photo
          </button>
        </div>
      )}

      {status === "error" && errorMsg && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-xl flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
          <p className="text-sm text-destructive">{errorMsg}</p>
        </div>
      )}
    </div>
  );
};

interface EndSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionToken: string;
  sessionId?: string;
}

const API_BASE_URL = "https://qwipsvc.up.railway.app";

type LockStatus = "idle" | "checking" | "locked" | "unlocked" | "error";

interface StepProps {
  onCheck: (checked: boolean) => void;
  checked: boolean;
}

interface RatingStepProps {
  rating: number;
  onRatingChange: (rating: number) => void;
}

interface LockStepProps {
  lockStatus: LockStatus;
  onCheckLock: () => void;
}

// Success Modal Component
const SuccessModal = ({ onClose }: { onClose: () => void }) => {
  const timerRef = useRef<number | null>(null);
  
  const handleRedirect = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    window.location.href = "https://qwip.co.uk";
  };

  useEffect(() => {
    // Start auto-redirect timer
    timerRef.current = window.setTimeout(() => {
      window.location.href = "https://qwip.co.uk";
    }, 2000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop - does nothing on click */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="relative bg-card rounded-2xl border border-border shadow-2xl w-full max-w-sm overflow-hidden"
      >
        {/* Close button - does NOT cancel redirect */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-muted rounded-lg transition-colors z-10"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>

        {/* Content */}
        <div className="px-6 py-10 text-center">
          {/* Success icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
            className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-10 h-10 text-green-500" strokeWidth={1.5} />
          </motion.div>

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-2xl font-bold text-foreground mb-2"
          >
            Session ended
          </motion.h2>

          {/* Body text */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-muted-foreground mb-1"
          >
            Thanks for booking with qwip!
          </motion.p>

          {/* Supportive line */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xs text-muted-foreground/70 mb-8"
          >
            Redirecting you to the home page…
          </motion.p>

          {/* Primary action button */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            onClick={handleRedirect}
            className="w-full py-3.5 px-6 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-medium transition-colors"
          >
            Go to home now
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

const Step1PersonalItems = ({ onCheck, checked }: StepProps) => (
  <div className="space-y-6">
    <div className="text-center space-y-2">
      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="text-3xl">🎒</span>
      </div>
      <h3 className="text-xl font-semibold text-foreground">Collect personal items</h3>
      <p className="text-muted-foreground">
        Take all your belongings with you (laptop, charger, bottle, etc.)
      </p>
    </div>
    <label className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl cursor-pointer hover:bg-muted transition-colors">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onCheck(e.target.checked)}
        className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
      />
      <span className="font-medium text-foreground">Done</span>
    </label>
  </div>
);

const Step2CleanPod = ({ onCheck, checked }: StepProps) => (
  <div className="space-y-6">
    <div className="text-center space-y-2">
      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="text-3xl">🧹</span>
      </div>
      <h3 className="text-xl font-semibold text-foreground">Leave the pod clean</h3>
      <p className="text-muted-foreground">
        Dispose of rubbish and leave the pod tidy for the next person.
      </p>
    </div>
    <label className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl cursor-pointer hover:bg-muted transition-colors">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onCheck(e.target.checked)}
        className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
      />
      <span className="font-medium text-foreground">Done</span>
    </label>
  </div>
);

const Step3LockPod = ({ lockStatus, onCheckLock }: LockStepProps) => (
  <div className="space-y-6">
    <div className="text-center space-y-2">
      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
        {lockStatus === "locked" ? (
          <Lock className="w-8 h-8 text-primary" />
        ) : (
          <LockOpen className="w-8 h-8 text-primary" />
        )}
      </div>
      <h3 className="text-xl font-semibold text-foreground">Lock the pod</h3>
      <p className="text-muted-foreground">
        Please lock the pod before ending your session.
      </p>
    </div>

    <button
      onClick={onCheckLock}
      disabled={lockStatus === "checking"}
      className="w-full py-3 px-6 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
    >
      {lockStatus === "checking" ? (
        <>
          <Loader2 className="w-5 h-5 animate-spin" />
          Checking…
        </>
      ) : (
        "Check lock status"
      )}
    </button>

    {/* Lock status indicator */}
    {lockStatus !== "idle" && lockStatus !== "checking" && (
      <div className={`p-4 rounded-xl flex items-center gap-3 ${
        lockStatus === "locked" 
          ? "bg-green-500/10 border border-green-500/20" 
          : lockStatus === "unlocked"
          ? "bg-amber-500/10 border border-amber-500/20"
          : "bg-destructive/10 border border-destructive/20"
      }`}>
        {lockStatus === "locked" ? (
          <>
            <Check className="w-5 h-5 text-green-500" />
            <span className="font-medium text-green-600">Locked ✅</span>
          </>
        ) : lockStatus === "unlocked" ? (
          <>
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <span className="font-medium text-amber-600">Unlocked ⚠️</span>
          </>
        ) : (
          <>
            <AlertTriangle className="w-5 h-5 text-destructive" />
            <span className="font-medium text-destructive">Couldn't check lock status. Try again.</span>
          </>
        )}
      </div>
    )}

    {/* Extra guidance when unlocked */}
    {lockStatus === "unlocked" && (
      <div className="p-4 bg-muted/50 rounded-xl">
        <p className="text-sm text-muted-foreground">
          If it says unlocked, enter your access code on the keypad and press the Yale button to lock.
        </p>
      </div>
    )}
  </div>
);

const Step4ConfirmCharges = ({ onCheck, checked }: StepProps) => (
  <div className="space-y-6">
    <div className="text-center space-y-2">
      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="text-3xl">💳</span>
      </div>
      <h3 className="text-xl font-semibold text-foreground">Confirm charges stop now</h3>
      <p className="text-muted-foreground">
        Ending your session stops charges immediately.
      </p>
    </div>
    <label className="flex items-center gap-3 p-4 bg-muted/50 rounded-xl cursor-pointer hover:bg-muted transition-colors">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onCheck(e.target.checked)}
        className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
      />
      <span className="font-medium text-foreground">I understand</span>
    </label>
  </div>
);

const RATING_LABELS = [
  "Very dissatisfied",
  "Dissatisfied", 
  "Neutral",
  "Satisfied",
  "Very satisfied"
];

const Step5RateSession = ({ rating, onRatingChange }: RatingStepProps) => (
  <div className="space-y-6">
    <div className="text-center space-y-2">
      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <Star className="w-8 h-8 text-primary" />
      </div>
      <h3 className="text-xl font-semibold text-foreground">Rate your session</h3>
      <p className="text-muted-foreground">
        How was your experience today?
      </p>
    </div>
    
    {/* Star Rating */}
    <div className="flex justify-center gap-2">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          onClick={() => onRatingChange(star)}
          className="p-1 transition-transform hover:scale-110 focus:outline-none"
          aria-label={`Rate ${star} stars - ${RATING_LABELS[star - 1]}`}
        >
          <Star
            className={`w-10 h-10 transition-colors ${
              star <= rating 
                ? "fill-amber-400 text-amber-400" 
                : "text-muted-foreground/30"
            }`}
          />
        </button>
      ))}
    </div>
    
    {/* Label under stars */}
    <div className="text-center">
      <p className={`text-sm font-medium transition-opacity ${
        rating > 0 ? "opacity-100" : "opacity-0"
      }`}>
        {rating > 0 ? RATING_LABELS[rating - 1] : "Select a rating"}
      </p>
    </div>
  </div>
);

export default function EndSessionModal({ isOpen, onClose, sessionToken, sessionId }: EndSessionModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [step1Checked, setStep1Checked] = useState(false);
  const [step2Checked, setStep2Checked] = useState(false);
  const [step4Checked, setStep4Checked] = useState(false);
  const [rating, setRating] = useState(0);
  const [lockStatus, setLockStatus] = useState<LockStatus>("idle");
  const [isEnding, setIsEnding] = useState(false);
  const [endError, setEndError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Checkout photo state
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoStatus, setPhotoStatus] = useState<PhotoUploadStatus>("idle");
  const [photoError, setPhotoError] = useState<string | null>(null);

  const totalSteps = 6;

  const resetAndClose = () => {
    setCurrentStep(0);
    setStep1Checked(false);
    setStep2Checked(false);
    setStep4Checked(false);
    setRating(0);
    setLockStatus("idle");
    setIsEnding(false);
    setEndError(false);
    setPhotoPreview(null);
    setPhotoUrl(null);
    setPhotoStatus("idle");
    setPhotoError(null);
    onClose();
  };

  const checkLockStatus = useCallback(async () => {
    setLockStatus("checking");
    try {
      const response = await fetch(`${API_BASE_URL}/api/lock-status?device_id=`, {
        method: "GET",
      });
      
      if (!response.ok) {
        throw new Error("Failed to check lock status");
      }

      const data = await response.json();
      if (data.is_locked) {
        setLockStatus("locked");
      } else {
        setLockStatus("unlocked");
      }
    } catch (error) {
      setLockStatus("error");
    }
  }, []);

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 0:
        return step1Checked;
      case 1:
        return step2Checked;
      case 2:
        return lockStatus === "locked";
      case 3:
        return photoStatus === "uploaded" && !!photoUrl;
      case 4:
        return step4Checked;
      case 5:
        return rating > 0;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1 && canProceed()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleEndSession = async () => {
    setIsEnding(true);
    setEndError(false);
    
    try {
      // Save rating to Supabase first (if we have a sessionId)
      if (sessionId && rating > 0) {
        const { error: ratingError } = await supabase
          .from("session_ratings")
          .insert({
            session_id: sessionId,
            rating: rating,
          });
        
        if (ratingError) {
          console.error("Failed to save rating:", ratingError);
          // Continue with end session even if rating fails
        }
      }
      
      // Then call end-session API
      const response = await fetch(`${API_BASE_URL}/api/end-session`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${sessionToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error("Failed to end session");
      }

      const data = await response.json();
      
      if (data.status === "success") {
        setShowSuccess(true);
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      setEndError(true);
      setIsEnding(false);
    }
  };

  // Handle closing the success modal (redirect still happens)
  const handleCloseSuccess = () => {
    setShowSuccess(false);
  };

  if (!isOpen) return null;

  // Show success modal if session ended successfully
  if (showSuccess) {
    return <SuccessModal onClose={handleCloseSuccess} />;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={resetAndClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-card rounded-2xl border border-border shadow-xl w-full max-w-md overflow-hidden"
      >
        {/* Close button */}
        <button
          onClick={resetAndClose}
          className="absolute top-4 right-4 p-2 hover:bg-muted rounded-lg transition-colors z-10"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>

        {/* Progress indicator */}
        <div className="px-6 pt-6 pb-2">
          <div className="flex items-center justify-center gap-2 mb-2">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === currentStep 
                    ? "bg-primary" 
                    : i < currentStep 
                    ? "bg-primary/50" 
                    : "bg-muted"
                }`}
              />
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground">
            Step {currentStep + 1} of {totalSteps}
          </p>
        </div>

        {/* Step content */}
        <div className="px-6 py-4 min-h-[320px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {currentStep === 0 && (
                <Step1PersonalItems checked={step1Checked} onCheck={setStep1Checked} />
              )}
              {currentStep === 1 && (
                <Step2CleanPod checked={step2Checked} onCheck={setStep2Checked} />
              )}
              {currentStep === 2 && (
                <Step3LockPod lockStatus={lockStatus} onCheckLock={checkLockStatus} />
              )}
              {currentStep === 3 && (
                <StepPhotoCheckout
                  sessionToken={sessionToken}
                  photoPreview={photoPreview}
                  setPhotoPreview={setPhotoPreview}
                  setPhotoUrl={setPhotoUrl}
                  status={photoStatus}
                  setStatus={setPhotoStatus}
                  errorMsg={photoError}
                  setErrorMsg={setPhotoError}
                />
              )}
              {currentStep === 4 && (
                <Step4ConfirmCharges checked={step4Checked} onCheck={setStep4Checked} />
              )}
              {currentStep === 5 && (
                <Step5RateSession rating={rating} onRatingChange={setRating} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Error message */}
        {endError && (
          <div className="mx-6 mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-xl">
            <p className="text-sm font-medium text-destructive">
              Error – contact support for more details.
            </p>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="px-6 pb-6 flex items-center gap-3">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="flex-1 py-3 px-6 bg-muted hover:bg-muted/80 text-foreground rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>

          {currentStep < totalSteps - 1 ? (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="flex-1 py-3 px-6 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleEndSession}
              disabled={!canProceed() || isEnding}
              className="flex-1 py-3 px-6 bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isEnding ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Ending…
                </>
              ) : (
                "End session"
              )}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
